// File: client/src/scenes/fines/MyFines.jsx
import React, { useMemo, useState } from "react";
import { Box, useTheme, useMediaQuery, Chip, Typography, Stack, TextField, MenuItem, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { gql, useQuery } from "@apollo/client";
import Header from "components/Header";

const GET_MEMBER_FINE_VIEW = gql`
  query MemberFineView {
    memberFineView(userId: null) {
      id
      userId
      groupId
      fine
      fineTypeId
      amountDue
      amountPaid
      dueDate
      statusId
      description
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

const STATUS = {
  1: { label: "Due", color: "warning" },
  2: { label: "Partially Paid", color: "info" },
  3: { label: "Paid", color: "success" },
};

const currency = (n) =>
  n == null ? "-" : `KES ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const MyFines = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [statusFilter, setStatusFilter] = useState("");

  const { data, loading, error, refetch } = useQuery(GET_MEMBER_FINE_VIEW, {
    fetchPolicy: "cache-and-network",
  });

  const filtered = useMemo(() => {
    const rows = data?.memberFineView ?? [];
    if (!statusFilter) return rows;
    return rows.filter((r) => String(r.statusId) === String(statusFilter));
  }, [data, statusFilter]);

  const columns = [
    { field: "sequence", headerName: "#", width: 70, sortable: false, filterable: false },
    { field: "fine", headerName: "Fine Type", flex: 0.7, minWidth: 160 },
    {
      field: "dueDate",
      headerName: "Due Date",
      flex: 0.8,
      minWidth: 140,
      valueFormatter: ({ value }) => (value ? new Date(value).toLocaleDateString() : "-"),
    },
    {
      field: "amountDue",
      headerName: "Amount Due",
      flex: 0.8,
      minWidth: 140,
      valueFormatter: ({ value }) => currency(value),
    },
    {
      field: "amountPaid",
      headerName: "Amount Paid",
      flex: 0.8,
      minWidth: 140,
      valueFormatter: ({ value }) => currency(value),
    },
    {
      field: "outstanding",
      headerName: "Outstanding",
      flex: 0.8,
      minWidth: 150,
      valueGetter: ({ row }) => {
        const due = Number(row.amountDue || 0);
        const paid = Number(row.amountPaid || 0);
        return Math.max(due - paid, 0);
      },
      valueFormatter: ({ value }) => currency(value),
    },
    {
      field: "statusId",
      headerName: "Status",
      flex: 0.7,
      minWidth: 140,
      renderCell: ({ value }) => {
        const s = STATUS[value] || { label: `Unknown (${value})`, color: "default" };
        return <Chip label={s.label} color={s.color} size="small" sx={{ fontWeight: 700 }} />;
      },
    },
    { field: "description", headerName: "Description", flex: 1.4, minWidth: 220 },
  ];

  if (error) {
    return <Box sx={{ m: { xs: "0.75rem", sm: "1rem 1.5rem", md: "1.5rem 2.5rem" }, minWidth: 0 }}><Typography color="error">Error: {error.message}</Typography></Box>;
  }

  return (
    <Box sx={{ m: { xs: "0.75rem", sm: "1rem 1.5rem", md: "1.5rem 2.5rem" }, minWidth: 0 }}>
      <Header title="MY FINES" subtitle="Fines assigned to you" />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" justifyContent="space-between" mb={2}>
        <TextField
          select
          label="Filter by Status"
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ width: 220 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="1">Due</MenuItem>
          <MenuItem value="2">Partially Paid</MenuItem>
          <MenuItem value="3">Paid</MenuItem>
        </TextField>

        <Button variant="outlined" onClick={() => refetch()} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Box
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
        }}
      >
        <DataGrid
          loading={loading}
          rows={filtered.map((row, index) => ({ ...row, sequence: index + 1 }))}
          getRowId={(row) => row.id}
          columns={columns}
        
          columnVisibilityModel={
            isMobile
              ? {
                description: false,
                amountPaid: false,
                dueDate: false,
              }
              : undefined
          }/>
      </Box>
    </Box>
  );
};

export default MyFines;
